import { useKeycloak } from '@react-keycloak/web';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export function useKeycloakToken() {
  const { keycloak, initialized } = useKeycloak();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const intervalIdRef = useRef<any>(null);
  const loginInProgressRef = useRef<boolean>(false);

  useEffect(() => {
    const checkSession = async () => {
      if (!initialized) {
        return;
      }

      if (!keycloak.authenticated) {
        setShowLoginModal(true);
        return;
      }

      // Try to refresh the token if it's expired or about to expire
      try {
        const refreshed = await keycloak.updateToken(30); // 30s min validity
        if (!refreshed && keycloak.isTokenExpired()) {
          setShowLoginModal(true);
        } else {
          setShowLoginModal(false);
        }
      } catch (err) {
        console.error('Keycloak token refresh failed:', err);
        setShowLoginModal(true);
      }
    };

    // Check session immediately
    checkSession();

    // Set up a periodic session check
    intervalIdRef.current = setInterval(checkSession, 10000); // every 10s

    // Set up the onTokenExpired event handler
    keycloak.onTokenExpired = async () => {
      if (!initialized || loginInProgressRef.current) {
        sessionStorage.clear();
        keycloak.logout();
        return;
      }
      try {
        const refreshed = await keycloak.updateToken(30);
        if (!refreshed && keycloak.isTokenExpired()) {
          setShowLoginModal(true);
        }
      } catch (err) {
        setShowLoginModal(true);
      }
      // Handlers remain active for the component's lifetime
    };

    // Set up the onAuthError event handler
    keycloak.onAuthError = (error) => {
      if (!initialized || loginInProgressRef.current) {
        return;
      }
      setShowLoginModal(true); // Show the login modal on authentication error
      // Handlers remain active for the component's lifetime
    };

    // Clean up the interval on unmount
    return () => {
      clearInterval(intervalIdRef.current); // Clear the interval
    };
  }, [initialized, keycloak]); // Only run this effect when keycloak or initialized changes

  const handleLogin = () => {
    if (keycloak) {
      loginInProgressRef.current = true;
      keycloak.login();
    }
  };

  return [keycloak.token, showLoginModal, handleLogin];
}

export function LoginModal({ showLoginModal, handleLogin }: any) {
  const { t } = useTranslation();

  if (!showLoginModal) {
    return null;
  }

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto bg-medium-gray bg-opacity-80">
      <div className="opacity-100 flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <div className="z-60 opacity-100 inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="text-center bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <h3 className="text-2xl leading-6 font-medium text-black">
              {t("Session Expired")}
            </h3>
            <div className="mt-2">
              <p className="text-secondary-gray">
                {t("Your session has expired. Please log in again.")}
              </p>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className="font-bold w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-dark-green text-base text-white hover:bg-status-green focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={handleLogin}
              >
                {t("Log In")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}